import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './VoiceSelectionModal.module.scss';
import AvatarVoicePreview from '../../../../features/Editor/components/AvatarModal/AvatarVoicePreview';
import CloseButton from '../../../../components/CloseButton/CloseButton';
import { useTranslation } from 'react-i18next';
import { CustomButton } from '../../../../components/Button/Button';
import ContainerWithScroll from '../../../../components/ContainerWithScroll/ContainerWithScroll';
import Spinner from '../../../../../common/components/Spinner';
import cx from 'classnames';
import * as Sentry from '@sentry/react';

const VoiceSelectionModal = ({ open, onClose, onSelect }) => {
    const [voicesList, setVoicesList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedVoiceId, setSelectedVoiceId] = useState(null);
    const audioRef = useRef(null);
    const [currentPlayingVoiceId, setCurrentPlayingVoiceId] = useState(null);
    const scrollContainerRef = useRef(null);
    const { t } = useTranslation();

    const handleClose = () => {
        setSelectedVoiceId(null);
        onClose();
    }

    const handleSelect = useCallback(async () => {
        try {
            setLoading(true);
            await onSelect(selectedVoiceId);
        } catch (e) {
            console.log('Error voice selection', e);
            Sentry.captureException(e);
        } finally {
            setLoading(false);
            handleClose();
        }
    }, [selectedVoiceId]);

    const handlePlayPauseVoice = useCallback((id) => {
        if (!audioRef.current) {
            audioRef.current = new Audio();
            audioRef.current.addEventListener('ended', () => {
                console.log('ended');
                setCurrentPlayingVoiceId(null);
            });
        }
        if (currentPlayingVoiceId === id) {
            if (audioRef.current.paused) {
                audioRef.current.play()
            } else {
                audioRef.current.pause()
                setCurrentPlayingVoiceId(null);
                audioRef.current.currentTime = 0;
            }
        } else {
            !audioRef.current.paused && audioRef.current.pause();
            const voiceSource = voicesList.find(voice => voice.voice_id === id).preview_audio;
            audioRef.current.src = voiceSource;
            setCurrentPlayingVoiceId(id);
            audioRef.current.play();
        }
    }, [currentPlayingVoiceId, voicesList]);

    useEffect(() => {
        if (!open) return;

        const loadResources = async () => {
            const voices = [
                {
                    "voice_id": "en-US-EchoTurboMultilingualNeural",
                    "gender": "Male",
                    "name": "Echo",
                    "preview_audio": "https://dev-static-video-resources.s3.eu-west-1.amazonaws.com/manual-uploads/echoturbo-preview.wav",
                },
                {
                    "voice_id": "en-US-AmandaMultilingualNeural",
                    "gender": "Female",
                    "name": "Amanda",
                    "preview_audio": "https://dev-static-video-resources.s3.eu-west-1.amazonaws.com/manual-uploads/amanda-preview.wav",
                }
            ]

            setVoicesList(voices);
        }

        loadResources();

        return () => {
            !audioRef?.current?.paused && audioRef.current?.pause();
        }
    }, [open])

    if (!open) return null;

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modal}>
                {loading ? (
                    <div className={styles.loading}>
                        <Spinner width='40px' height='40px' size='3px' className={styles.spinner} />
                        <h1 className={styles.title}>{t('clip_organizer.generate_avatar.loading')}</h1>
                    </div>
                ) : (
                    <>
                        <div className={styles.header}>
                            <h1 className={styles.title}>{t('subtitles.editor.select_voice')}</h1>
                            <CloseButton onClose={handleClose} />
                        </div>
                        <ContainerWithScroll ref={scrollContainerRef}>
                            <div className={cx(styles.warningContainer, styles.show)}>
                                <p className={styles.warning}>{t('subtitles.editor.redirect_warning')}</p>
                            </div>
                            <h2 className={styles.sectionTitle}>{t('clip_organizer.generate_avatar.select_voice')}</h2>
                            <div className={styles.voiceSection}>
                                {voicesList?.map((voice, index) => (
                                    <AvatarVoicePreview
                                        key={index}
                                        voice={voice}
                                        onSelect={setSelectedVoiceId}
                                        selected={voice.voice_id === selectedVoiceId}
                                        playPauseVoice={handlePlayPauseVoice}
                                        currentPlayingVoiceId={currentPlayingVoiceId}
                                    />
                                ))}
                            </div>
                        </ContainerWithScroll>
                        <div className={styles.footer}>
                            <CustomButton label={t('general.cancel')} variant='secondary' onClick={handleClose} />
                            <CustomButton label={t('subtitles.editor.generate')} variant='primary' onClick={handleSelect} disabled={!selectedVoiceId} />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default VoiceSelectionModal; 